import { Tooltip } from "@mui/material";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { EClientData, IClient, IClientData } from "dto/IClient";
import { useContext, useEffect, useState } from "react";
import Input from "components/Input";
import ClientsContext from "contexts/ClientsContext";
import clientService from "api/clientService";
import StarIcon from "assets/icons/newStar.svg?react";
import StarBorderIcon from "assets/icons/starBorder.svg?react";
import EditIcon from "assets/icons/edite.svg?react";
import CopyIcon from "assets/icons/copye.svg?react";
import { formatToFormat } from "utils/formatToFormat";
import { useParams } from "react-router-dom";
import { isValidEmail } from "utils/isValidEmail";
import { isValidPhoneNumber } from "utils/isValidPhoneNumber";
import { useTranslation } from "react-i18next";

const ClientInformation = ({
  isEditMode,
  data,
  onClick,
  handleSave,
  handleChangeIsMain,
  handleCancel,
}: {
  isEditMode: boolean;
  data: IClientData;
  onClick(): void;
  handleSave(value: string): void;
  handleChangeIsMain(): void;
  handleCancel(): void;
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(data.Value);

  useEffect(() => {
    setValue(data?.Value);
  }, [data]);

  if (isEditMode) {
    return (
      <div className={styles.edit}>
        <Input
          label={t("common.edit")}
          onChange={(text) => setValue(text)}
          value={value}
          onRightClick={() => handleSave(value)}
          focus
          // onBlur={handleCancel}
          onRightText={t("common.save")}
        />
      </div>
    );
  }

  return (
    <div className={styles.valueWrapper}>
      <div title={value} className={styles.name}>
        {data.Type === EClientData.PHONE
          ? formatToFormat(value, "### ### ###")
          : value}
      </div>
      <div className={styles.options}>
        <Tooltip title={t("common.edit")}>
          <div>
            <EditIcon className={styles.icon} onClick={onClick} />
          </div>
        </Tooltip>
        <Tooltip title={t("common.copy")}>
          <div>
            <CopyIcon
              className={styles.icon}
              onClick={() => {
                navigator.clipboard.writeText(value ?? "");
              }}
            />
          </div>
        </Tooltip>
        {data.IsMain && <StarIcon className={` ${styles.icon}`} />}
        {!data.IsMain && (
          <StarBorderIcon
            onClick={handleChangeIsMain}
            className={`${styles.icon}`}
          />
        )}
      </div>
    </div>
  );
};

const ClientData: FC<Props> = ({ client, type }) => {
  const { t } = useTranslation();
  const { clientId } = useParams();
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [data, setData] = useState<IClientData[]>([]);
  const { clients, setClients } = useContext(ClientsContext);
  const { mutate: updateClient } = clientService.useNewUpdateClient();
  useEffect(() => {
    setData(client?.Data?.filter((c) => c.Type === type.type));
  }, [client.Data]);

  const handleUpdateContext = (newData: IClientData[]) => {
    const copyOfClients = [...clients];
    const element = copyOfClients.find((i) => i.Id === client.Id);
    if (!element) return;
    element.Data = newData;
    setClients(copyOfClients);
  };

  const handleUpdateDatabase = (newData: IClientData[]) => {
    const clientToUpdate: IClient = { ...client };
    clientToUpdate.Data = newData;
    updateClient(clientToUpdate, {});
  };

  const handleSave = (newValue: string) => {
    if (newValue === " ") {
      setData(data.filter((_, index) => index !== editIndex));
      return;
    }

    console.log(type);

    if (type.type === EClientData.EMAIL && !isValidEmail(newValue)) {
      setData(data.filter((_, index) => index !== editIndex));
      return;
    }

    if (
      type.type === EClientData.PHONE &&
      isValidPhoneNumber(newValue.replaceAll(" ", ""))
    ) {
      setData(data.filter((_, index) => index !== editIndex));
      return;
    }

    const copyOfData = [...data];
    const element = copyOfData.find((_, index) => index === editIndex);
    if (!element) return;
    element.Value = newValue;
    setData(copyOfData);
    let copyOfClientData = [...client.Data];
    copyOfClientData = copyOfClientData.filter((c) => c.Type !== type.type);
    copyOfClientData = [copyOfClientData, data].flat();
    handleUpdateContext(copyOfClientData);
    handleUpdateDatabase(copyOfClientData);
    setEditIndex(null);
  };

  const handleAddNew = () => {
    setEditIndex(data.length);
    setData([
      ...data,
      {
        IsMain: false,
        Type: type.type,
        Value: "",
      },
    ]);
  };

  const handleChangeIsMain = (selectedIndex: number) => {
    let copyOfData = [...data];
    copyOfData = copyOfData.map((d, index) => {
      if (index === selectedIndex) {
        d.IsMain = true;
      } else {
        d.IsMain = false;
      }

      return d;
    });

    setData(copyOfData);

    let copyOfClientData = [...client.Data];
    copyOfClientData = copyOfClientData.filter((c) => c.Type !== type.type);
    copyOfClientData = [copyOfClientData, data].flat();
    handleUpdateContext(copyOfClientData);
    handleUpdateDatabase(copyOfClientData);
  };

  useEffect(() => {
    setEditIndex(null);
  }, [clientId]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        {<type.Icon className={styles.titleIcon} />}
        {type.label}
      </div>
      <div className={styles.list}>
        {data?.map((d, index) => (
          <ClientInformation
            handleCancel={() => {
              setData(data.filter((_, index) => index !== editIndex));

              setEditIndex(null);
            }}
            isEditMode={editIndex === index}
            data={d}
            key={index}
            onClick={() => setEditIndex(index)}
            handleSave={handleSave}
            handleChangeIsMain={() => handleChangeIsMain(index)}
          />
        ))}
        {editIndex === null && (
          <div className={styles.valueWrapper}>
            <div className={styles.name}>-</div>
            <div className={styles.options}>
              <Tooltip title={t("common.edit")}>
                <div>
                  <EditIcon className={styles.icon} onClick={handleAddNew} />
                </div>
              </Tooltip>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientData;
